import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  FieldHint,
  RequiredMark,
  clearFieldError,
  fieldClass,
} from '@/client/pages/Admin/shared/form-field';
import { ChipMultiSelect } from '@/client/pages/Admin/shared/ChipMultiSelect';
import { OutletFavicon } from './OutletFavicon';
import type { SourceTier, TraditionalMedium } from '@/client/types/media';
import { MEDIUM_LABEL } from '@/client/types/media';
import type {
  ApiMediaOutlet,
  ContactPoint,
  ContactPointKind,
  LinkPolicy,
} from '@/client/types/marketing/api';
import {
  MEDIA_BEAT_LABEL,
  MEDIA_BEATS,
  type MediaBeat,
} from '@/client/types/marketing/strategy';
import {
  useCreateMediaOutletMutation,
  useUpdateMediaOutletMutation,
} from '@/client/api/marketing/sources';
import { getApiErrorMessage } from '@/client/utils/errors';

/**
 * Add or edit a media outlet, writing to
 * `/api/admin/v2/marketing/sources/outlets`.
 *
 * The feed URL is the one field here with a background consequence: the ingest
 * task polls it every 15 minutes and turns what it finds into Radar coverage.
 * Everything else on this form is a fact about the outlet; that one starts a
 * recurring job, so it says so.
 */

const MEDIUMS: TraditionalMedium[] = [
  'print',
  'online_news',
  'tv',
  'radio',
  'podcast',
  'wire',
  'other',
];

type Ownership = 'owned' | 'partner_media' | 'news_outlet';

const OWNERSHIP_OPTIONS: { value: Ownership; label: string }[] = [
  { value: 'news_outlet', label: 'News outlet' },
  { value: 'partner_media', label: 'Partner media' },
  { value: 'owned', label: 'Owned' },
];

const CURRENCIES = ['GHS', 'USD', 'NGN', 'KES'];

/** Markets we track plus where pan-African outlets are based. */
const COUNTRY_CODES = ['GH', 'NG', 'KE', 'ZA', 'UG', 'CI', 'SN', 'RW', 'TZ', 'GB', 'US', 'CH', 'NL', 'AE'];
const AUDIENCE_OPTIONS = [
  ...COUNTRY_CODES.slice(0, 9).map((c) => ({ value: c, label: c })),
  { value: 'AFRICA', label: 'All Africa' },
  { value: 'GLOBAL', label: 'Global' },
];

const LINK_POLICY_OPTIONS: { value: LinkPolicy; label: string }[] = [
  { value: 'unknown', label: 'Not known yet' },
  { value: 'sponsored', label: 'Marked sponsored' },
  { value: 'nofollow', label: 'Nofollow' },
  { value: 'dofollow', label: 'Dofollow' },
];

const CONTACT_KINDS: { value: ContactPointKind; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'whatsapp', label: 'WhatsApp' },
];

/** Saved list, or the legacy single email as a one-row list. */
const initialContacts = (outlet: ApiMediaOutlet | null): ContactPoint[] => {
  if (outlet?.contact_points?.length) return outlet.contact_points.map((p) => ({ ...p }));
  if (outlet?.contact_email) {
    return [{ kind: 'email', value: outlet.contact_email, label: null, preferred: true }];
  }
  return [];
};

/**
 * Every email, phone and WhatsApp number found for an outlet. One per kind is
 * preferred; the server keeps exactly one and copies the preferred email into
 * `contact_email`, which outlet emails default to.
 */
function ContactPointsEditor({
  value,
  onChange,
}: {
  value: ContactPoint[];
  onChange: (next: ContactPoint[]) => void;
}) {
  const update = (i: number, patch: Partial<ContactPoint>) =>
    onChange(value.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const prefer = (i: number) =>
    onChange(value.map((p, j) => (p.kind === value[i].kind ? { ...p, preferred: j === i } : p)));
  const add = (kind: ContactPointKind) =>
    onChange([
      ...value,
      { kind, value: '', label: null, preferred: !value.some((p) => p.kind === kind) },
    ]);

  return (
    <div className="space-y-2">
      {value.length === 0 && <p className="text-xs text-gray-400">No contacts yet.</p>}
      {value.map((p, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
          <select
            value={p.kind}
            onChange={(e) =>
              update(i, { kind: e.target.value as ContactPointKind, preferred: false })
            }
            className={`${inputClass} w-28 shrink-0`}
            aria-label="Contact type"
          >
            {CONTACT_KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
          <input
            type={p.kind === 'email' ? 'email' : 'tel'}
            value={p.value}
            onChange={(e) => update(i, { value: e.target.value })}
            className={`${inputClass} min-w-0 flex-1`}
            placeholder={p.kind === 'email' ? 'desk@example.com' : '+233 20 000 0000'}
            aria-label="Contact value"
          />
          <input
            value={p.label ?? ''}
            onChange={(e) => update(i, { label: e.target.value || null })}
            className={`${inputClass} w-28 shrink-0`}
            placeholder="Label"
            aria-label="Contact label"
          />
          <label className="flex shrink-0 items-center gap-1 text-xs text-gray-600">
            <input
              type="radio"
              name={`preferred-${p.kind}`}
              checked={p.preferred}
              onChange={() => prefer(i)}
            />
            Preferred
          </label>
          <button
            type="button"
            onClick={() => onChange(value.filter((_, j) => j !== i))}
            className="shrink-0 text-xs text-red-600 hover:underline"
          >
            Remove
          </button>
        </div>
      ))}
      <div className="flex gap-3 text-xs">
        {CONTACT_KINDS.map((k) => (
          <button
            key={k.value}
            type="button"
            onClick={() => add(k.value)}
            className="text-blue-700 hover:underline"
          >
            + {k.label}
          </button>
        ))}
      </div>
    </div>
  );
}

interface OutletFormDialogProps {
  open: boolean;
  outlet: ApiMediaOutlet | null;
  onClose: () => void;
}

type FormState = {
  name: string;
  medium: TraditionalMedium;
  tier: SourceTier;
  ownership: Ownership;
  useAsDestination: boolean;
  useAsListen: boolean;
  active: boolean;
  region: string;
  country: string;
  audienceCountries: string[];
  website: string;
  feedUrl: string;
  contacts: ContactPoint[];
  pitchEmail: string;
  reach: string;
  ownerName: string;
  beats: MediaBeat[];
  notes: string;
  doNotPitch: boolean;
  /** Major units as typed; stored as minor units. */
  price: string;
  priceCurrency: string;
  linkPolicy: LinkPolicy;
  selfServeUrl: string;
  rateSourceUrl: string;
  rateCheckedAt: string;
};

type FormErrors = Partial<Record<keyof FormState, string>>;

const BEAT_CHIP_OPTIONS = MEDIA_BEATS.map((b) => ({
  value: b,
  label: MEDIA_BEAT_LABEL[b],
}));

const knownBeats = (value: unknown): MediaBeat[] => {
  const raw = Array.isArray(value) ? (value as unknown[]).map(String) : [];
  return raw.filter((t): t is MediaBeat => (MEDIA_BEATS as readonly string[]).includes(t));
};

function toForm(outlet: ApiMediaOutlet | null): FormState {
  if (!outlet) {
    return {
      name: '',
      medium: 'online_news',
      tier: 2,
      ownership: 'news_outlet',
      useAsDestination: true,
      useAsListen: true,
      active: true,
      region: '',
      country: 'GH',
      audienceCountries: ['GH'],
      website: '',
      feedUrl: '',
      contacts: [],
      pitchEmail: '',
      reach: '',
      ownerName: '',
      beats: [],
      notes: '',
      doNotPitch: false,
      price: '',
      priceCurrency: 'GHS',
      linkPolicy: 'unknown',
      selfServeUrl: '',
      rateSourceUrl: '',
      rateCheckedAt: '',
    };
  }
  const beats = Array.isArray(outlet.beats) ? outlet.beats : [];
  return {
    name: outlet.name,
    medium: outlet.medium as TraditionalMedium,
    tier: (outlet.tier as SourceTier) ?? 2,
    ownership: (outlet.ownership as Ownership) || 'news_outlet',
    useAsDestination: !!outlet.use_as_destination,
    useAsListen: !!outlet.use_as_listen,
    active: !!outlet.active,
    region: outlet.region ?? '',
    country: outlet.country ?? '',
    audienceCountries: outlet.audience_countries ?? [],
    website: outlet.website ?? '',
    feedUrl: outlet.feed_url ?? '',
    contacts: initialContacts(outlet),
    pitchEmail: outlet.pitch_email ?? '',
    reach: outlet.reach != null ? String(outlet.reach) : '',
    ownerName: outlet.owner_name ?? '',
    beats: knownBeats(beats),
    notes: outlet.notes ?? '',
    doNotPitch: !!outlet.do_not_pitch,
    price: outlet.price_amount != null ? String(outlet.price_amount / 100) : '',
    priceCurrency: outlet.price_currency ?? 'GHS',
    linkPolicy: outlet.link_policy ?? 'unknown',
    selfServeUrl: outlet.self_serve_url ?? '',
    rateSourceUrl: outlet.rate_source_url ?? '',
    rateCheckedAt: outlet.rate_checked_at ?? '',
  };
}

function Field({
  label,
  children,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-gray-600">{label}</span>
      {children}
    </label>
  );
}

const inputClass =
  'h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';

export function OutletFormDialog({ open, outlet, onClose }: OutletFormDialogProps) {
  const isEdit = !!outlet;
  const [form, setForm] = useState<FormState>(() => toForm(outlet));
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const createOutlet = useCreateMediaOutletMutation();
  const updateOutlet = useUpdateMediaOutletMutation();
  const saving = createOutlet.isPending || updateOutlet.isPending;

  useEffect(() => {
    if (open) {
      setForm(toForm(outlet));
      setFormErrors({});
    }
  }, [open, outlet]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFormErrors((e) => clearFieldError(e, key));
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: FormErrors = {};
    if (!form.name.trim()) {
      errors.name = 'Outlet name is required';
    }

    const reachRaw = form.reach.trim().replace(/,/g, '');
    const reach = reachRaw === '' ? undefined : Number(reachRaw);
    if (reach != null && (Number.isNaN(reach) || reach < 0)) {
      errors.reach = 'Reach must be a non-negative number';
    }

    const feedUrl = form.feedUrl.trim();
    if (feedUrl && !/^https?:\/\//i.test(feedUrl)) {
      errors.feedUrl = 'The feed URL must start with http:// or https://';
    }

    if (form.country.trim() && !/^[A-Za-z]{2}$/.test(form.country.trim())) {
      errors.country = 'Use a 2-letter country code, e.g. GH';
    }

    const priceRaw = form.price.trim().replace(/,/g, '');
    const price = priceRaw === '' ? null : Number(priceRaw);
    if (price != null && (Number.isNaN(price) || price < 0)) {
      errors.price = 'Price must be a non-negative number';
    }
    for (const key of ['selfServeUrl', 'rateSourceUrl'] as const) {
      const v = form[key].trim();
      if (v && !/^https?:\/\//i.test(v)) errors[key] = 'Must start with http:// or https://';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error('Fix the highlighted fields before saving');
      return;
    }

    // TINYINT columns: send 0/1, not true/false.
    const payload = {
      name: form.name.trim(),
      medium: form.medium,
      tier: form.tier,
      ownership: form.ownership,
      use_as_destination: form.useAsDestination ? 1 : 0,
      use_as_listen: form.useAsListen ? 1 : 0,
      active: form.active ? 1 : 0,
      region: form.region.trim() || null,
      country: form.country.trim().toUpperCase() || null,
      audience_countries: form.audienceCountries,
      website: form.website.trim() || null,
      feed_url: feedUrl || null,
      contact_points: form.contacts.filter((p) => p.value.trim()),
      pitch_email: form.pitchEmail.trim() || null,
      reach: reach ?? null,
      owner_name: form.ownerName.trim() || null,
      beats: form.beats,
      notes: form.notes.trim() || null,
      do_not_pitch: form.doNotPitch ? 1 : 0,
      price_amount: price == null ? null : Math.round(price * 100),
      price_currency: price == null ? null : form.priceCurrency,
      link_policy: form.linkPolicy,
      self_serve_url: form.selfServeUrl.trim() || null,
      rate_source_url: form.rateSourceUrl.trim() || null,
      rate_checked_at: form.rateCheckedAt || null,
    };

    const handlers = {
      onSuccess: () => {
        toast.success(isEdit ? 'Outlet updated' : 'Outlet added');
        onClose();
      },
      onError: (err: unknown) => toast.error(getApiErrorMessage(err, 'Could not save the outlet')),
    };

    if (isEdit && outlet) {
      updateOutlet.mutate({ moid: outlet.moid, ...payload }, handlers);
    } else {
      createOutlet.mutate(payload, handlers);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {(form.website || form.feedUrl || form.name) && (
              <OutletFavicon
                name={form.name || 'Outlet'}
                website={form.website}
                feedUrl={form.feedUrl}
                size="sm"
              />
            )}
            {isEdit ? 'Edit outlet' : 'Add outlet'}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update this press or broadcast outlet.'
              : 'Add a press, broadcast, or digital outlet to the directory.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} noValidate className="space-y-4">
          <Field label={<><span>Name</span> <RequiredMark /></>}>
            <input
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              className={fieldClass(inputClass, formErrors.name)}
              aria-invalid={!!formErrors.name}
              placeholder="Graphic Business"
            />
            <FieldHint error={formErrors.name} />
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Medium">
              <select
                value={form.medium}
                onChange={(e) => set('medium', e.target.value as TraditionalMedium)}
                className={inputClass}
              >
                {MEDIUMS.map((m) => (
                  <option key={m} value={m}>
                    {MEDIUM_LABEL[m]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Ownership">
              <select
                value={form.ownership}
                onChange={(e) => set('ownership', e.target.value as Ownership)}
                className={inputClass}
              >
                {OWNERSHIP_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Tier">
              <select
                value={form.tier}
                onChange={(e) => set('tier', Number(e.target.value) as SourceTier)}
                className={inputClass}
              >
                <option value={1}>Tier 1</option>
                <option value={2}>Tier 2</option>
                <option value={3}>Tier 3</option>
              </select>
            </Field>
            <Field label="Reach">
              <input
                value={form.reach}
                onChange={(e) => set('reach', e.target.value)}
                className={fieldClass(inputClass, formErrors.reach)}
                aria-invalid={!!formErrors.reach}
                placeholder="240000"
                inputMode="numeric"
              />
              <FieldHint error={formErrors.reach} />
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Region">
              <input
                value={form.region}
                onChange={(e) => set('region', e.target.value)}
                className={inputClass}
                placeholder="City or region, e.g. Accra"
              />
            </Field>
            <Field label="Website">
              <div className="flex items-center gap-2">
                <OutletFavicon
                  name={form.name || 'Outlet'}
                  website={form.website}
                  feedUrl={form.feedUrl}
                  size="sm"
                />
                <input
                  value={form.website}
                  onChange={(e) => set('website', e.target.value)}
                  className={`${inputClass} min-w-0 flex-1`}
                  placeholder="https://"
                />
              </div>
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Country (based in)">
              <input
                value={form.country}
                onChange={(e) => set('country', e.target.value.toUpperCase())}
                className={fieldClass(inputClass, formErrors.country)}
                aria-invalid={!!formErrors.country}
                list="outlet-country-codes"
                maxLength={2}
                placeholder="GH"
              />
              <datalist id="outlet-country-codes">
                {COUNTRY_CODES.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
              <FieldHint error={formErrors.country} />
            </Field>
            <Field label="Read in">
              <ChipMultiSelect
                options={AUDIENCE_OPTIONS}
                value={form.audienceCountries}
                onChange={(next) => set('audienceCountries', next)}
                aria-label="Audience countries"
              />
            </Field>
          </div>

          <Field label="RSS / Atom feed">
            <input
              value={form.feedUrl}
              onChange={(e) => set('feedUrl', e.target.value)}
              className={fieldClass(inputClass, formErrors.feedUrl)}
              aria-invalid={!!formErrors.feedUrl}
              placeholder="https://example.com/feed"
            />
            <FieldHint error={formErrors.feedUrl} />
            <span className="mt-1 block text-[11px] text-gray-400">
              Polled every 15 minutes; new articles become Radar coverage. Leave empty to
              track this outlet without ingesting it.
              {outlet?.feed_last_fetched_at
                ? ` Last read ${new Date(outlet.feed_last_fetched_at).toLocaleString()}.`
                : ''}
            </span>
          </Field>

          <div className="space-y-1.5">
            <span className="text-xs font-medium text-gray-600">Contacts</span>
            <ContactPointsEditor value={form.contacts} onChange={(next) => set('contacts', next)} />
          </div>

          <Field label="Pitch email (news desk)">
            <input
              type="email"
              value={form.pitchEmail}
              onChange={(e) => set('pitchEmail', e.target.value)}
              className={inputClass}
              placeholder="tips@example.com"
            />
            <span className="mt-1 block text-[11px] text-gray-400">
              Outlet emails go here first, then to the preferred email above.
            </span>
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Owner (internal)">
              <input
                value={form.ownerName}
                onChange={(e) => set('ownerName', e.target.value)}
                className={inputClass}
                placeholder="PR / Comms"
              />
            </Field>
            <Field label="Beats">
              <ChipMultiSelect
                options={BEAT_CHIP_OPTIONS}
                value={form.beats}
                onChange={(next) => set('beats', next)}
                aria-label="Outlet beats"
              />
            </Field>
          </div>

          <fieldset className="space-y-3 rounded-lg border border-gray-100 p-3">
            <legend className="px-1 text-xs font-medium text-gray-600">
              Sponsored article rate
            </legend>
            <div className="grid gap-3 sm:grid-cols-[1fr_6rem_1fr]">
              <Field label="Price per article">
                <input
                  value={form.price}
                  onChange={(e) => set('price', e.target.value)}
                  className={fieldClass(inputClass, formErrors.price)}
                  aria-invalid={!!formErrors.price}
                  inputMode="decimal"
                  placeholder="Blank if not published"
                />
                <FieldHint error={formErrors.price} />
              </Field>
              <Field label="Currency">
                <select
                  value={form.priceCurrency}
                  onChange={(e) => set('priceCurrency', e.target.value)}
                  className={inputClass}
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Our link">
                <select
                  value={form.linkPolicy}
                  onChange={(e) => set('linkPolicy', e.target.value as LinkPolicy)}
                  className={inputClass}
                >
                  {LINK_POLICY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Self-serve booking URL">
                <input
                  value={form.selfServeUrl}
                  onChange={(e) => set('selfServeUrl', e.target.value)}
                  className={fieldClass(inputClass, formErrors.selfServeUrl)}
                  aria-invalid={!!formErrors.selfServeUrl}
                  placeholder="https://"
                />
                <FieldHint error={formErrors.selfServeUrl} />
              </Field>
              <Field label="Price source">
                <input
                  value={form.rateSourceUrl}
                  onChange={(e) => set('rateSourceUrl', e.target.value)}
                  className={fieldClass(inputClass, formErrors.rateSourceUrl)}
                  aria-invalid={!!formErrors.rateSourceUrl}
                  placeholder="Rate card or advertise page"
                />
                <FieldHint error={formErrors.rateSourceUrl} />
              </Field>
            </div>
            <Field label="Price last checked">
              <input
                type="date"
                value={form.rateCheckedAt}
                onChange={(e) => set('rateCheckedAt', e.target.value)}
                className={`${inputClass} sm:w-48`}
              />
            </Field>
            <span className="block text-[11px] text-gray-400">
              A paid link should be marked sponsored or nofollow; a paid dofollow link is a
              Google spam-policy risk.
            </span>
          </fieldset>

          <Field label="Notes">
            <textarea
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              className="min-h-[72px] w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm outline-none ring-blue-500 focus:ring-2"
              placeholder="Pitch guidance, embargo rules…"
            />
          </Field>

          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.useAsDestination}
                onChange={(e) => set('useAsDestination', e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              Use as destination
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.useAsListen}
                onChange={(e) => set('useAsListen', e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              Use as listen
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => set('active', e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              Active
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.doNotPitch}
                onChange={(e) => {
                  const doNotPitch = e.target.checked;
                  setForm((prev) => ({
                    ...prev,
                    doNotPitch,
                    useAsDestination: doNotPitch ? false : prev.useAsDestination,
                  }));
                }}
                className="h-4 w-4 rounded border-gray-300"
              />
              Do not pitch
            </label>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add outlet'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
